import React, { useRef, useState, useCallback, useMemo } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';
import { cn } from '../../utils/cn';

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  /** Color of the spotlight effect */
  spotlightColor?: string;
  /** Intensity of the spotlight (0-1) */
  spotlightIntensity?: number;
  /** Size of the spotlight in pixels */
  spotlightSize?: number;
  /** Enable 3D tilt effect on hover */
  tiltEnabled?: boolean;
  /** Maximum tilt angle in degrees */
  tiltMaxAngle?: number;
  /** Enable animated border effect */
  animatedBorder?: boolean;
  /** Hover lift distance in pixels */
  hoverLift?: number;
  /** Enable glow effect on hover */
  glowOnHover?: boolean;
}

// Spring config for smooth animations
const springConfig = { stiffness: 300, damping: 30 };

export const SpotlightCard: React.FC<SpotlightCardProps> = ({
  children,
  className = "",
  spotlightColor = "rgba(24, 240, 233, 0.15)",
  spotlightIntensity = 1,
  spotlightSize = 600,
  tiltEnabled = true,
  tiltMaxAngle = 10,
  animatedBorder = false,
  hoverLift = 4,
  glowOnHover = false,
  ...props
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  // Motion values for smooth tilt animation
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Spring-animated rotation values
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [tiltMaxAngle, -tiltMaxAngle]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-tiltMaxAngle, tiltMaxAngle]), springConfig);

  // Compute spotlight opacity based on intensity
  const spotlightOpacity = useMemo(() => isHovered ? spotlightIntensity : 0, [isHovered, spotlightIntensity]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;

    const rect = divRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setPosition({ x, y });

    // Normalize mouse position for tilt (-0.5 to 0.5)
    if (tiltEnabled) {
      const normalizedX = (x / rect.width) - 0.5;
      const normalizedY = (y / rect.height) - 0.5;
      mouseX.set(normalizedX);
      mouseY.set(normalizedY);
    }
  }, [tiltEnabled, mouseX, mouseY]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    // Reset tilt on mouse leave
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  // Determine if we need to use motion.div for animations
  const needsMotion = tiltEnabled || hoverLift > 0 || glowOnHover;

  const cardContent = (
    <>
      {/* Spotlight effect layer */}
      <div
        className="pointer-events-none absolute -inset-px transition-opacity duration-300"
        style={{
          opacity: spotlightOpacity,
          background: `radial-gradient(${spotlightSize}px circle at ${position.x}px ${position.y}px, ${spotlightColor}, transparent 40%)`,
        }}
        aria-hidden="true"
      />

      {/* Animated border shimmer effect */}
      {animatedBorder && (
        <div
          className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-500"
          style={{
            opacity: isHovered ? 1 : 0,
          }}
          aria-hidden="true"
        >
          <div className="absolute inset-0 rounded-xl animated-border" />
        </div>
      )}

      {/* Content layer */}
      <div className="relative h-full z-10">
        {children}
      </div>
    </>
  );

  const baseClasses = cn(
    'relative overflow-hidden rounded-xl border border-slate-800 bg-st-card',
    'transition-all duration-300 ease-out',
    'hover:border-st-primary/40',
    glowOnHover && 'hover:shadow-lg hover:shadow-st-primary/20',
    className
  );

  // Use motion.div for 3D tilt effect
  if (needsMotion) {
    return (
      <motion.div
        ref={divRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={baseClasses}
        style={{
          transformStyle: tiltEnabled ? 'preserve-3d' : undefined,
          perspective: tiltEnabled ? 1000 : undefined,
          rotateX: tiltEnabled ? rotateX : 0,
          rotateY: tiltEnabled ? rotateY : 0,
        }}
        whileHover={{
          y: -hoverLift,
          transition: { duration: 0.3 }
        }}
        {...(props as unknown as React.ComponentProps<typeof motion.div>)}
      >
        {cardContent}
      </motion.div>
    );
  }

  // Fallback to regular div if no motion needed
  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={baseClasses}
      {...props}
    >
      {cardContent}
    </div>
  );
};

// Bento grid variant with enhanced spotlight
export const BentoCard: React.FC<SpotlightCardProps & {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  featured?: boolean;
}> = ({
  children,
  className = '',
  size = 'md',
  featured = false,
  ...props
}) => {
  const sizeClasses = {
    sm: '',
    md: 'md:col-span-1 md:row-span-1',
    lg: 'md:col-span-2 md:row-span-1',
    xl: 'md:col-span-2 md:row-span-2',
  };

  return (
    <SpotlightCard
      className={cn(
        sizeClasses[size],
        featured && 'border-st-primary/30 bg-gradient-to-br from-st-card to-st-primary/5',
        className
      )}
      spotlightIntensity={featured ? 1.2 : 1}
      tiltEnabled={true}
      tiltMaxAngle={featured ? 6 : 10}
      animatedBorder={featured}
      glowOnHover={true}
      hoverLift={featured ? 6 : 4}
      {...props}
    >
      {children}
    </SpotlightCard>
  );
};

